import React, { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
// Ensure this path is correct in your project
import qr from "./Images/QR.jpeg"; 
import payment1 from "./Images/gpay.png";
import payment2 from "./Images/Phonepe.png";
import payment3 from "./Images/UPI.png";
import payment4 from "./Images/BHIM.png";

import { 
  Plus, Trash2, ChevronLeft, Loader2, Save, 
  Smartphone, ShieldCheck, Mail, MapPin, Phone
} from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// CONFIGURATION
const API_URL = "https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/bills";

export default function GenerateBillForm() {
  const printRef = useRef();
  const location = useLocation(); 
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("Save & Print"); 

  // --- 1. Settings ---
  const [execId, setExecId] = useState(() => localStorage.getItem('dgl_exec_id') || 'A');

  // --- 2. Bill Data State ---
  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceNo: "PENDING...", 
    date: new Date().toISOString().split('T')[0],
    storeName: "",
    address: "",
    mobile: "",
    email: "",
    salesExecutive: location.state?.userName || "" 
  });

  const [items, setItems] = useState([
    { id: Date.now(), description: "", qty: 1, unitPrice: 0 }
  ]);

  const [discount, setDiscount] = useState(0);

  // --- 3. Calculations ---
  const validItems = items.map(item => {
    const parsedQty = parseFloat(item.qty) || 0;
    const parsedPrice = parseFloat(item.unitPrice) || 0;

    return {
      ...item,
      qty: parsedQty,
      unitPrice: parsedPrice,
      amount: parsedQty * parsedPrice
    };
  });

  const subTotal = validItems.reduce((acc, item) => acc + item.amount, 0);
  const grandTotal = subTotal - (parseFloat(discount) || 0);

  // --- 4. Handlers ---
  const handleDetailChange = (e) => setInvoiceDetails({ ...invoiceDetails, [e.target.name]: e.target.value });
  
  const handleItemChange = (id, field, value) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addItem = () => setItems([...items, { id: Date.now(), description: "", qty: 1, unitPrice: 0 }]);
  
  const removeItem = (id) => {
    if(items.length > 1) setItems(items.filter(item => item.id !== id));
  };

  const handleExecIdChange = (e) => {
      const val = e.target.value.toUpperCase();
      setExecId(val);
      localStorage.setItem('dgl_exec_id', val);
  };

  // --- 5. Save & Upload Logic ---
  const handleSaveAndUpload = async () => {
    if (!invoiceDetails.storeName) return alert("Please enter Store Name");
    if (!invoiceDetails.salesExecutive) return alert("Sales Executive Name is missing");
    if (items.length === 0) return alert("Please add at least one item");

    setIsSaving(true);

    try {
        setSaveStatus("Generating ID...");
        
        const billPayload = {
            execId,
            storeName: invoiceDetails.storeName,
            address: invoiceDetails.address,
            mobile: invoiceDetails.mobile,
            email: invoiceDetails.email,
            salesExecutive: invoiceDetails.salesExecutive,
            items: validItems.map(i => ({
                description: i.description,
                qty: i.qty,
                unitPrice: i.unitPrice,
                amount: i.amount
            })),
            discount: parseFloat(discount),
            subTotal,
            grandTotal
        };

        const createResponse = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(billPayload)
        });

        const createdBill = await createResponse.json();
        if (!createResponse.ok) throw new Error(createdBill.error || "Failed to create bill");

        setInvoiceDetails(prev => ({
            ...prev,
            invoiceNo: createdBill.invoiceNo,
            date: createdBill.createdAt?.split('T')[0] || prev.date
        }));

        await new Promise(resolve => setTimeout(resolve, 800));

        setSaveStatus("Generating PDF...");
        const element = printRef.current;
        
        const canvas = await html2canvas(element, { 
            scale: 2, 
            useCORS: true,
            logging: false,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight
        });
        
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = 210;
        const scaledHeight = (canvas.height * pdfWidth) / canvas.width;

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, scaledHeight);
        const pdfBlob = pdf.output('blob');

        setSaveStatus("Uploading...");
        const formData = new FormData();
        formData.append("pdf", pdfBlob, `Invoice_${createdBill.invoiceNo}.pdf`);

        const uploadResponse = await fetch(`${API_URL}/${createdBill._id}/pdf`, {
            method: "PUT",
            body: formData 
        });

        if (!uploadResponse.ok) throw new Error("Failed to upload PDF");

        alert(`Success! Bill ${createdBill.invoiceNo} saved.`);

    } catch (error) {
        console.error("Error:", error);
        alert("Error: " + error.message);
    } finally {
        setIsSaving(false);
        setSaveStatus("Save & Print");
    }
  };

  return (
    <div className="h-screen bg-slate-950 text-slate-200 font-sans flex flex-col md:flex-row overflow-hidden">
      
      {/* --- LEFT SIDE: FORM (UNCHANGED) --- */}
      <div className="w-full md:w-5/12 h-full flex flex-col border-r border-slate-800 bg-slate-950/50">
        
        {/* Header */}
        <div className="p-4 border-b border-slate-800 bg-slate-950 flex items-center justify-between shrink-0 z-20">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium">
            <ChevronLeft size={16} /> Back
          </Link>
          <button 
              onClick={handleSaveAndUpload}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium shadow-lg shadow-indigo-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
              {isSaving ? (
                  <><Loader2 size={18} className="animate-spin" /> {saveStatus}</>
              ) : (
                  <><Save size={18} /> Save & Process</>
              )}
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 pb-24">
            
            {/* Device Config */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex justify-between items-center">
                <div>
                    <h3 className="text-xs font-semibold text-indigo-400 uppercase tracking-wider">Device Config</h3>
                    <p className="text-[10px] text-slate-500">Invoice Sequence Prefix</p>
                </div>
                <div className="flex flex-col items-center">
                    <label className="text-[9px] text-slate-400 uppercase font-semibold mb-1">Exec ID</label>
                    <input type="text" value={execId} onChange={handleExecIdChange} maxLength={2} className="w-12 bg-slate-950 border border-slate-600 rounded px-2 py-1 text-xs text-center text-white focus:border-indigo-500 uppercase font-semibold" />
                </div>
            </div>

            {/* Customer Details */}
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-white border-l-4 border-indigo-500 pl-3">Customer Details</h3>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <label className="text-[10px] uppercase text-slate-500 font-semibold">Store Name</label>
                        <input type="text" name="storeName" value={invoiceDetails.storeName} onChange={handleDetailChange} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-sm focus:border-indigo-500 outline-none" placeholder="e.g. Malavahalli Biryani Centre" />
                    </div>
                    <div className="col-span-2">
                        <label className="text-[10px] uppercase text-slate-500 font-semibold">Address</label>
                        <textarea name="address" value={invoiceDetails.address} onChange={handleDetailChange} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-sm focus:border-indigo-500 outline-none h-16 resize-none" placeholder="Enter full address" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase text-slate-500 font-semibold">Mobile</label>
                        <input type="text" name="mobile" value={invoiceDetails.mobile} onChange={handleDetailChange} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-sm focus:border-indigo-500 outline-none" placeholder="9986..." />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase text-slate-500 font-semibold">Email (Opt)</label>
                        <input type="email" name="email" value={invoiceDetails.email} onChange={handleDetailChange} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-sm focus:border-indigo-500 outline-none" placeholder="@example.com" />
                    </div>
                </div>
            </div>

            {/* Sales Info */}
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-white border-l-4 border-emerald-500 pl-3">Invoice Meta</h3>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-[10px] uppercase text-slate-500 font-semibold">Sales Executive</label>
                        <input type="text" name="salesExecutive" value={invoiceDetails.salesExecutive} onChange={handleDetailChange} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-sm text-emerald-400 font-semibold focus:border-emerald-500 outline-none" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase text-slate-500 font-semibold">Invoice Status</label>
                        <input type="text" value={invoiceDetails.invoiceNo} readOnly className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-xs text-slate-400 font-mono cursor-not-allowed" />
                    </div>
                </div>
            </div>

            {/* Items */}
            <div className="space-y-4">
                <div className="flex justify-between items-center border-l-4 border-amber-500 pl-3">
                    <h3 className="text-sm font-semibold text-white">Items List</h3>
                    <button onClick={addItem} className="text-xs flex items-center gap-1 bg-slate-800 text-white px-3 py-1.5 rounded hover:bg-slate-700 border border-slate-700 transition-colors"><Plus size={14}/> Add New</button>
                </div>
                
                <div className="space-y-3">
                    {items.map((item, index) => (
                    <div key={item.id} className="grid grid-cols-12 gap-2 items-start bg-slate-900 p-3 rounded-lg border border-slate-800">
                        <div className="col-span-1 pt-2 text-center text-xs text-slate-500 font-mono">{index + 1}</div>
                        <div className="col-span-5">
                            <label className="text-[9px] text-slate-500 font-semibold block mb-1">Item Name</label>
                            <input type="text" className="w-full bg-transparent border-b border-slate-700 text-sm text-slate-200 outline-none focus:border-amber-500 pb-1" value={item.description} onChange={(e) => handleItemChange(item.id, 'description', e.target.value)} placeholder="Description"/>
                        </div>
                        <div className="col-span-2">
                            <label className="text-[9px] text-slate-500 font-semibold block mb-1 text-center">Qty</label>
                            <input type="number" className="w-full bg-slate-950 border border-slate-700 rounded p-1.5 text-sm text-center text-white focus:border-amber-500 outline-none" value={item.qty} onChange={(e) => handleItemChange(item.id, 'qty', e.target.value)}/>
                        </div>
                        <div className="col-span-3">
                            <label className="text-[9px] text-slate-500 font-semibold block mb-1 text-center">Price</label>
                            <input type="number" className="w-full bg-slate-950 border border-slate-700 rounded p-1.5 text-sm text-center text-white focus:border-amber-500 outline-none" value={item.unitPrice} onChange={(e) => handleItemChange(item.id, 'unitPrice', e.target.value)}/>
                        </div>
                        <div className="col-span-1 pt-6 flex justify-end">
                            <button onClick={() => removeItem(item.id)} className="text-slate-600 hover:text-rose-500 transition-colors"><Trash2 size={16} /></button>
                        </div>
                    </div>
                    ))}
                </div>
            </div>

            {/* Discount */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center justify-between">
                <label className="text-sm font-semibold text-slate-400">Total Discount (₹)</label>
                <input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-32 bg-slate-950 border border-slate-700 rounded-lg p-2 text-right text-sm font-semibold text-white focus:border-emerald-500 outline-none" />
            </div>

        </div>
      </div>

      {/* --- RIGHT SIDE: PREVIEW --- */}
      <div className="w-full md:w-7/12 h-full bg-slate-900/50 p-4 md:p-8 overflow-y-auto flex justify-center items-start shadow-inner">
        
        {/* PRINT CONTAINER WRAPPER */}
        <div className="relative shadow-2xl">
            
            {/* --- STANDARD INK-SAVER DESIGN START --- */}
            {/* Changed min-h to half A4 (148.5mm) and removed forced full page height */}
            <div 
            ref={printRef}
            className="bg-white text-gray-800 w-[210mm] h-fit min-h-[148.5mm] relative flex flex-col font-sans border border-gray-100"
            >
                {/* 1. Brand Header */}
                <div className="px-8 pt-8 pb-4">
                    <div className="flex justify-between items-start">
                        <div className="flex gap-4">
                            <div className="w-14 h-14 bg-white rounded flex items-center justify-center">
                                <img src="/B2B.png" alt="Logo" className="object-contain w-full h-full" />
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900 uppercase tracking-tight">Daily Go Pvt Ltd</h1>
                                <div className="text-[10px] text-gray-600 space-y-0.5 mt-1">
                                    <p className="flex items-center gap-1"><MapPin size={10}/> Registered Office: No 6, Gubbala Main Road,<br/> BWSSBB PIPELINE,
                                        Singapore Gardens and<br/> Green Fields, Subramanyapura Pura Post, Gubalalla,</p>
                                    <p className="pl-3.5">Bangalore, Karnataka - 560062</p>
                                    <p className="flex items-center gap-3">
                                        <span className="flex items-center gap-1"><Phone size={10}/> 9739777166</span>
                                        <span className="flex items-center gap-1"><Mail size={10}/> office-info@dailygolive.in</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <h2 className="text-2xl font-semibold text-gray-800 tracking-tight uppercase">Invoice</h2>
                            <p className="text-[10px] font-medium text-gray-500 mt-1 uppercase tracking-widest">Original Recipient</p>
                        </div>
                    </div>

                    {/* Divider with ID */}
                    <div className="flex justify-between items-end border-b border-gray-300 mt-4 pb-2">
                        <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                           GSTIN: <span className="text-gray-900">URP</span> &nbsp;|&nbsp; State: <span className="text-gray-900">Karnataka (29)</span>
                        </div>
                        <div className="flex gap-6 text-xs">
                            <div>
                                <span className="text-gray-500 font-medium text-[10px] uppercase block">Invoice No</span>
                                <span className="font-mono font-medium text-gray-900 text-base">{invoiceDetails.invoiceNo}</span>
                            </div>
                            <div className="text-right">
                                <span className="text-gray-500 font-medium text-[10px] uppercase block">Date Issued</span>
                                <span className="font-medium text-gray-900 text-base">{invoiceDetails.date}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Customer Section */}
                <div className="px-8 py-2 grid grid-cols-2 gap-8">
                    {/* Bill To */}
                    <div className="border border-gray-200 p-4 rounded">
                        <h3 className="text-[10px] font-medium text-gray-500 uppercase tracking-widest mb-2">Billed To</h3>
                        <div className="text-sm space-y-0.5">
                            <p className="text-base font-medium text-gray-900 leading-tight">{invoiceDetails.storeName || "Store Name"}</p>
                            <p className="text-gray-600 leading-snug text-xs">{invoiceDetails.address || "No Address Provided"}</p>
                            <p className="text-gray-800 pt-2 mt-1 border-t border-gray-100 flex items-center gap-2 text-xs">
                                <Smartphone size={12} className="text-gray-400"/> {invoiceDetails.mobile}
                            </p>
                        </div>
                    </div>

                    {/* Invoice Details */}
                    <div className="pt-1">
                         <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-widest mb-2 text-right">Invoice Meta</h3>
                         <div className="space-y-2">
                            <div className="flex justify-between border-b border-dashed border-gray-200 pb-1">
                                <span className="text-xs text-gray-500">Sales Executive</span>
                                <span className="text-xs font-medium text-gray-900 text-right">{invoiceDetails.salesExecutive}</span>
                            </div>
                            <div className="flex justify-between border-b border-dashed border-gray-200 pb-1">
                                <span className="text-xs text-gray-500">GSTIN</span>
                                <span className="text-xs font-medium text-gray-900 text-right">URP</span>
                            </div>
                            <div className="flex justify-between border-b border-dashed border-gray-200 pb-1">
                                <span className="text-xs text-gray-500">Place of Supply</span>
                                <span className="text-xs font-medium text-gray-900 text-right">Bangalore</span>
                            </div>
                         </div>
                    </div>
                </div>

                {/* 3. Table */}
                <div className="px-8 mt-2">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-y border-gray-300 text-gray-700 text-[10px] uppercase tracking-wider">
                                <th className="py-2 px-2 font-medium w-12 text-center">#</th>
                                <th className="py-2 px-2 font-medium">Description</th>
                                <th className="py-2 px-2 font-medium text-center w-20">Qty</th>
                                <th className="py-2 px-2 font-medium text-right w-24">Rate</th>
                                <th className="py-2 px-2 font-medium text-right w-24">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="text-xs">
                            {validItems.map((item, index) => (
                                <tr key={item.id} className="border-b border-gray-100 last:border-0">
                                    <td className="py-2 px-2 text-center text-gray-500">{index + 1}</td>
                                    <td className="py-2 px-2 font-medium text-gray-800">{item.description}</td>
                                    <td className="py-2 px-2 text-center text-gray-600">{item.qty}</td>
                                    <td className="py-2 px-2 text-right text-gray-600">₹{item.unitPrice.toFixed(2)}</td>
                                    <td className="py-2 px-2 text-right font-medium text-gray-900">₹{item.amount.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* 4. Footer Section - Removed mt-auto so it flows naturally up */}
                <div className="mt-8 px-8 pb-8">
                    <div className="flex justify-between items-start border-t border-gray-300 pt-6">
                        
                        {/* LEFT: Terms & Payment */}
                        <div className="w-7/12 pr-6">
                            <div className="flex gap-4 mb-4">
                                <div className="border border-gray-200 p-1 rounded bg-white w-20 h-20 shrink-0">
                                    <img src={qr} alt="QR" className="w-full h-full object-contain" />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-gray-900 mb-1 flex items-center gap-1">
                                        <ShieldCheck size={14} className="text-gray-600"/> Scan to Pay
                                    </p>
                                    <div className="flex gap-2 items-center opacity-80 mt-1 grayscale">
                                        <img src={payment4} alt="BHIM" className="h-6 w-auto object-contain" />
                                        <img src={payment3} alt="UPI" className="h-5 w-auto object-contain" />
                                        <img src={payment1} alt="GPay" className="h-6 w-auto object-contain" />
                                        <img src={payment2} alt="PhonePe" className="h-6 w-auto object-contain" />
                                    </div>
                                </div>
                            </div>
                            
                            <div>
                                <h4 className="text-[10px] font-medium text-gray-700 uppercase mb-1">Terms & Conditions</h4>
                                <ul className="text-[9px] text-gray-500 space-y-0.5 list-disc list-inside leading-tight">
                                    <li>Payment is due immediately. No credit facilities available.</li>
                                    <li>Goods once sold cannot be returned.</li>
                                    <li>Disputes subject to Bangalore Jurisdiction.</li>
                                </ul>
                            </div>
                        </div>

                        {/* RIGHT: Totals & Sign */}
                        <div className="w-5/12 pl-6 border-l border-gray-100">
                            <div className="space-y-2 text-xs">
                                <div className="flex justify-between text-gray-600">
                                    <span>Sub Total</span>
                                    <span className="text-gray-900">₹{subTotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                    <span>Discount</span>
                                    <span className="text-gray-800">- ₹{discount}</span>
                                </div>
                                <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-900 uppercase">Total</span>
                                    <span className="text-lg font-semibold text-gray-900">₹{grandTotal.toFixed(2)}</span>
                                </div>
                            </div>

                            <div className="mt-12 pt-4 text-right relative">
                                <div className="absolute top-0 right-0 w-24 border-t border-gray-400"></div>
                                <p className="text-[10px] font-medium text-gray-900 uppercase">Authorized Signatory</p>
                                <p className="text-[9px] text-gray-500">For Daily Go Pvt Ltd</p>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Bottom Bar */}
                <div className="border-t border-gray-200 text-gray-500 text-[9px] py-1 text-center tracking-wide">
                    Thank you for your business
                </div>

            </div>
            {/* --- STANDARD INK-SAVER DESIGN END --- */}
        </div>
      </div>
    </div>
  );
}